import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { KINDRED_ICONS } from "./icons";
import { KINDRED_OPTIONS } from "./utils";
import {
  HelpCircle
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface KindredStepProps {
  setCreatureTypes: (types: string[]) => void;
  setColorIdentity: (colors: string[]) => void;
  handleNextStep: (strategyOverride?: string, colorIdentityOverride?: string[], themesOverride?: string, creatureTypeOverride?: string, cardTypeOverride?: string) => void;
}

export const KindredStep = ({
  setCreatureTypes,
  setColorIdentity,
  handleNextStep
}: KindredStepProps) => {
  const [selectedKindred, setSelectedKindred] = useState<string>("");

  const handleKindredSelect = (kindredId: string) => {
    const kindred = KINDRED_OPTIONS.find(k => k.id === kindredId);
    if (kindred) {
      setSelectedKindred(kindredId);
      setCreatureTypes([kindred.creatureType]);
      setColorIdentity(kindred.colorIdentity);
      handleNextStep('kindred', kindred.colorIdentity, undefined, kindred.creatureType);
    }
  };

  return (
    <div className="space-y-4 md:space-y-8">
      <div className="text-center">
        <h2 className="text-lg md:text-xl font-bold">Choose Your Kindred</h2>
        <p className="text-muted-foreground text-xs md:text-sm max-w-md mx-auto">
          What type of creature will you lead to victory?
        </p>
      </div>

      <Card>
        <CardContent className="p-3 md:p-4 space-y-3 md:space-y-4">
          <div className="flex items-center justify-center gap-2 mb-1 md:mb-2">
            <Label className="font-medium text-sm md:text-base">Select a Creature Type</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="w-4 h-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">Each creature type has its own unique strengths and synergies.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
            {KINDRED_OPTIONS.map((kindred) => (
              <Card
                key={kindred.id}
                className={`cursor-pointer transition-all duration-200 hover:scale-105 touch-manipulation ${
                  selectedKindred === kindred.id
                    ? 'ring-2 ring-primary bg-primary/5'
                    : 'hover:bg-muted/50'
                }`}
                onClick={() => handleKindredSelect(kindred.id)}
              >
                <CardContent className="p-2 md:p-3 text-center space-y-1 md:space-y-2">
                  <div className={`w-8 h-8 md:w-10 md:h-10 mx-auto rounded-full flex items-center justify-center ${
                    selectedKindred === kindred.id
                      ? 'bg-primary/20 text-primary'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    <div className="w-4 h-4 md:w-6 md:h-6 flex items-center justify-center">
                      {KINDRED_ICONS[kindred.id as keyof typeof KINDRED_ICONS]}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-xs md:text-sm mb-0.5 md:mb-1 leading-tight">{kindred.name}</h3>
                    <p className="text-xs text-muted-foreground leading-tight">{kindred.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
